import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../lib/cn';

export type ButtonVariante = 'primaer' | 'sekundaer' | 'dezent' | 'gefahr' | 'gefahrStark';
export type ButtonGroesse = 'sm' | 'md' | 'lg';

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>;

export interface ButtonProps extends NativeButtonProps {
  variante?: ButtonVariante;
  groesse?: ButtonGroesse;
  /** Icon links vom Text. */
  icon?: ReactNode;
  /** Icon rechts vom Text. */
  iconRechts?: ReactNode;
  ladend?: boolean;
  volleBreite?: boolean;
}

const VARIANTEN: Record<ButtonVariante, string> = {
  primaer:
    'bg-accent text-accent-fg shadow-accent hover:bg-accent-hover active:bg-accent-active border border-transparent',
  sekundaer:
    'bg-surface text-fg border border-border-strong shadow-xs hover:bg-surface-2 active:bg-surface-3',
  dezent:
    'bg-transparent text-fg-muted border border-transparent hover:bg-surface-2 hover:text-fg active:bg-surface-3',
  gefahr:
    'bg-danger-soft text-danger-soft-fg border border-transparent hover:bg-danger hover:text-fg-invert',
  gefahrStark: 'bg-danger text-fg-invert border border-transparent shadow-xs hover:brightness-110 active:brightness-95',
};

const GROESSEN: Record<ButtonGroesse, string> = {
  sm: 'h-9 px-3 text-sm gap-2 rounded-md',
  md: 'h-11 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-12 px-5 text-base gap-3 rounded-lg',
};

export function Button({
  variante = 'primaer',
  groesse = 'md',
  icon,
  iconRechts,
  ladend = false,
  volleBreite = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const reduziert = useReducedMotion();
  const gesperrt = disabled || ladend;

  return (
    <motion.button
      type="button"
      whileTap={reduziert || gesperrt ? undefined : { scale: 0.985 }}
      transition={{ type: 'spring', stiffness: 500, damping: 32 }}
      disabled={gesperrt}
      aria-busy={ladend || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium',
        'transition-colors duration-fast ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        'disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none',
        VARIANTEN[variante],
        GROESSEN[groesse],
        volleBreite && 'w-full',
        className,
      )}
      {...rest}
    >
      {ladend ? <Spinner /> : icon}
      {children}
      {iconRechts}
    </motion.button>
  );
}

function Spinner() {
  return (
    <span
      className="h-4 w-4 animate-spin rounded-pill border-2 border-current border-t-transparent"
      aria-hidden="true"
    />
  );
}
